// Cómo se reserva: con cuánta antelación, cuántas noches, cuánta gente y a
// qué precio por noche. Cuatro distribuciones por tramos.

import type { HistoricoReservasResult } from '@/lib/types';
import { Tarjeta, BarrasTramos, fmtNum, fmtPct, fmtMoney } from './comun';

export default function SeccionComportamiento({ data }: { data: HistoricoReservasResult }) {
  const { kpis, grupo } = data;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <Tarjeta
        titulo="Antelación de la reserva"
        info="Días entre la fecha en que se hizo la reserva y la entrada (advance_days de Hostify), en reservas confirmadas. Entre paréntesis, el porcentaje sobre el total; a la derecha, la tasa de cancelación de las reservas hechas con esa antelación."
        extra={
          <span className="text-xs text-sereno-dark/50 whitespace-nowrap">
            media {fmtNum(kpis.antelacionMedia)} d. · mediana {fmtNum(kpis.antelacionMediana)} d.
          </span>
        }
      >
        <BarrasTramos
          filas={data.antelacion.map((t) => ({
            etiqueta: t.tramo,
            valor: t.reservas,
            texto: `${fmtNum(t.reservas)} (${fmtPct(t.pct)})`,
            detalle: t.reservas + t.canceladas > 0 ? `${fmtPct(t.tasaCancelacion)} cancel.` : undefined,
          }))}
        />
      </Tarjeta>

      <Tarjeta
        titulo="Duración de la estancia"
        info="Noches por reserva confirmada. Entre paréntesis, el porcentaje de reservas; a la derecha, qué parte de todas las noches vendidas aporta ese tramo."
        extra={<span className="text-xs text-sereno-dark/50 whitespace-nowrap">media {fmtNum(kpis.estanciaMedia, 1)} noches</span>}
      >
        <BarrasTramos
          filas={data.duracion.map((t) => ({
            etiqueta: t.tramo,
            valor: t.reservas,
            texto: `${fmtNum(t.reservas)} (${fmtPct(t.pct)})`,
            detalle: `${fmtPct(t.pctNoches)} noches`,
          }))}
        />
      </Tarjeta>

      <Tarjeta
        titulo="Tamaño del grupo"
        info="Huéspedes por reserva confirmada (campo guests de Hostify). Abajo, cuántas reservas traen niños o bebés según los campos children e infants, que no todos los canales rellenan."
        extra={<span className="text-xs text-sereno-dark/50 whitespace-nowrap">media {fmtNum(kpis.huespedesMedia, 1)} huéspedes</span>}
      >
        <BarrasTramos
          filas={grupo.tamano.map((t) => ({
            etiqueta: `${t.tramo} ${t.tramo === '1' ? 'huésped' : 'huéspedes'}`,
            valor: t.reservas,
            texto: `${fmtNum(t.reservas)} (${fmtPct(t.pct)})`,
          }))}
        />
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Mini label="Con niños" valor={fmtPct(grupo.pctConNinos)} />
          <Mini label="Con bebés" valor={fmtPct(grupo.pctConBebes)} />
          <Mini label="Adultos / reserva" valor={fmtNum(grupo.adultosMedia, 1)} />
          <Mini label="Niños / reserva" valor={fmtNum(grupo.ninosMedia, 1)} />
        </div>
      </Tarjeta>

      <Tarjeta
        titulo="Precio por noche"
        info="Precio de alojamiento de cada reserva dividido entre sus noches (sin limpieza ni extras), en reservas confirmadas con precio. Es la distribución detrás del ADR."
        extra={<span className="text-xs text-sereno-dark/50 whitespace-nowrap">ADR {fmtMoney(kpis.adr)}</span>}
      >
        <BarrasTramos
          filas={data.precioNoche.map((t) => ({
            etiqueta: t.tramo,
            valor: t.reservas,
            texto: `${fmtNum(t.reservas)} (${fmtPct(t.pct)})`,
          }))}
        />
      </Tarjeta>
    </div>
  );
}

function Mini({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-lg bg-sereno-blue/20 px-3 py-2">
      <p className="text-[11px] text-sereno-dark/50">{label}</p>
      <p className="font-semibold">{valor}</p>
    </div>
  );
}
